import { toast } from "sonner";
import { Download, History, Package } from "lucide-react";

const FuelTrackingSection = ({ ctx }) => {
  const {
    equipmentList,
    materials,
    openDetailedFuelHistory,
    outwardRecords,
    project
  } = ctx;
  return (
    <>
        <div>
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-heading font-bold text-xl text-slate-800">
              ⛽ Equipment Fuel Consumption Report
            </h3>
            <button
              onClick={() => {
                // Export fuel consumption report
                let csvContent = "data:text/csv;charset=utf-8,";
                csvContent += `KPM Infrastructure - Fuel Consumption Report\n`;
                csvContent += `Project: ${project?.name || 'N/A'}\n`;
                csvContent += `Generated: ${new Date().toLocaleString()}\n\n`;
                csvContent += `Equipment,Registration,Total Diesel (L),Total Petrol (L),Total Cost\n`;
                
                equipmentList.forEach((eq) => {
                  const eqFuelIssues = outwardRecords.filter(o => o.is_fuel_issue && o.equipment_id === eq.id);
                  const totalDiesel = eqFuelIssues.filter(o => o.fuel_type === 'Diesel' || o.fuel_type === 'HSD').reduce((sum, o) => sum + (o.quantity || 0), 0);
                  const totalPetrol = eqFuelIssues.filter(o => o.fuel_type === 'Petrol').reduce((sum, o) => sum + (o.quantity || 0), 0);
                  const material = materials.find(m => eqFuelIssues[0]?.material_id === m.id);
                  const rate = material?.rate || 0;
                  const totalCost = (totalDiesel + totalPetrol) * rate;
                  if (eqFuelIssues.length > 0) {
                    csvContent += `"${eq.name}",${eq.registration_number || 'N/A'},${totalDiesel.toFixed(2)},${totalPetrol.toFixed(2)},${totalCost.toFixed(2)}\n`;
                  }
                });
                
                const encodedUri = encodeURI(csvContent);
                const link = document.createElement("a");
                link.setAttribute("href", encodedUri);
                link.setAttribute("download", `Fuel_Report_${project?.name?.replace(/[^a-z0-9]/gi, '_') || 'Project'}_${new Date().toISOString().split('T')[0]}.csv`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                toast.success("Fuel consumption report exported");
              }}
              className="btn-primary flex items-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>Export Fuel Report</span>
            </button>
          </div>

          {/* Summary Cards */}
          <div className="grid md:grid-cols-4 gap-4 mb-6">
            {(() => {
              const fuelIssues = outwardRecords.filter(o => o.is_fuel_issue);
              const totalDiesel = fuelIssues.filter(o => o.fuel_type === 'Diesel' || o.fuel_type === 'HSD').reduce((sum, o) => sum + (o.quantity || 0), 0);
              const totalPetrol = fuelIssues.filter(o => o.fuel_type === 'Petrol').reduce((sum, o) => sum + (o.quantity || 0), 0);
              const equipmentWithFuel = [...new Set(fuelIssues.map(f => f.equipment_id))].length;
              
              return (
                <>
                  <div className="stat-card border-t-4 border-t-amber-500">
                    <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Total Diesel Issued</p>
                    <p className="font-heading font-black text-3xl text-amber-600 font-mono">{totalDiesel.toFixed(2)} L</p>
                  </div>
                  <div className="stat-card border-t-4 border-t-green-500">
                    <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Total Petrol Issued</p>
                    <p className="font-heading font-black text-3xl text-green-600 font-mono">{totalPetrol.toFixed(2)} L</p>
                  </div>
                  <div className="stat-card border-t-4 border-t-blue-500">
                    <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Equipment with Fuel Issues</p>
                    <p className="font-heading font-black text-3xl text-blue-600 font-mono">{equipmentWithFuel}</p>
                  </div>
                  <div className="stat-card border-t-4 border-t-purple-500">
                    <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Total Fuel Issues</p>
                    <p className="font-heading font-black text-3xl text-purple-600 font-mono">{fuelIssues.length}</p>
                  </div>
                </>
              );
            })()}
          </div>

          {/* Equipment-wise Fuel Consumption */}
          <div className="stat-card">
            <h4 className="font-heading font-bold text-lg text-slate-800 mb-4">Equipment-wise Fuel Consumption</h4>
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-300">
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Equipment</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Registration</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Diesel (L)</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Petrol (L)</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Total (L)</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Issues</th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Last Issue</th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {equipmentList.map((eq) => {
                  const eqFuelIssues = outwardRecords.filter(o => o.is_fuel_issue && o.equipment_id === eq.id);
                  if (eqFuelIssues.length === 0) return null;
                  
                  const totalDiesel = eqFuelIssues.filter(o => o.fuel_type === 'Diesel' || o.fuel_type === 'HSD').reduce((sum, o) => sum + (o.quantity || 0), 0);
                  const totalPetrol = eqFuelIssues.filter(o => o.fuel_type === 'Petrol').reduce((sum, o) => sum + (o.quantity || 0), 0);
                  const lastIssue = eqFuelIssues.sort((a, b) => new Date(b.date) - new Date(a.date))[0];
                  
                  return (
                    <tr 
                      key={eq.id} 
                      className="border-b border-slate-200 hover:bg-blue-50 cursor-pointer transition-colors"
                      onClick={() => openDetailedFuelHistory(eq)}
                      data-testid={`fuel-history-row-${eq.id}`}
                    >
                      <td className="py-3 px-4 font-medium text-primary">{eq.name}</td>
                      <td className="py-3 px-4 font-mono text-sm">{eq.equipment_id || eq.registration_number || eq.equipment_code || '-'}</td>
                      <td className="py-3 px-4 text-right font-mono text-sm text-amber-600 font-semibold">{totalDiesel.toFixed(2)}</td>
                      <td className="py-3 px-4 text-right font-mono text-sm text-green-600 font-semibold">{totalPetrol.toFixed(2)}</td>
                      <td className="py-3 px-4 text-right font-mono text-sm font-bold text-primary">{(totalDiesel + totalPetrol).toFixed(2)}</td>
                      <td className="py-3 px-4 text-right font-mono text-sm">{eqFuelIssues.length}</td>
                      <td className="py-3 px-4 text-center text-sm">{lastIssue?.date || '-'}</td>
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={(e) => { e.stopPropagation(); openDetailedFuelHistory(eq); }}
                          className="text-primary hover:text-primary/80 p-1"
                          title="View Fuel History"
                        >
                          <History className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {outwardRecords.filter(o => o.is_fuel_issue).length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <Package className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                <h3 className="font-heading font-bold text-lg mb-2">No Fuel Issues Recorded</h3>
                <p className="text-slate-600">Fuel issues to equipment will appear here when you record material outward for fuel items.</p>
              </div>
            )}
          </div>

          {/* Recent Fuel Issues */}
          {outwardRecords.filter(o => o.is_fuel_issue).length > 0 && (
            <div className="stat-card mt-6">
              <h4 className="font-heading font-bold text-lg text-slate-800 mb-4">Recent Fuel Issues</h4>
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-slate-300">
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Date</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Equipment</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Fuel Type</th>
                    <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Quantity</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Issued By</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {outwardRecords
                    .filter(o => o.is_fuel_issue)
                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                    .slice(0, 10)
                    .map((issue) => {
                      const equipment = equipmentList.find(e => e.id === issue.equipment_id);
                      return (
                        <tr key={issue.id} className="border-b border-slate-200 hover:bg-slate-50">
                          <td className="py-3 px-4 font-mono text-sm">{issue.date}</td>
                          <td className="py-3 px-4 font-medium">{equipment?.name || 'Unknown'}</td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              issue.fuel_type === 'Diesel' || issue.fuel_type === 'HSD' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'
                            }`}>
                              {issue.fuel_type}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right font-mono text-sm font-bold">{issue.quantity?.toFixed(2)} L</td>
                          <td className="py-3 px-4 text-sm">{issue.created_by || '-'}</td>
                          <td className="py-3 px-4 text-sm text-slate-600">{issue.remarks || '-'}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
        </div>
    </>
  );
};

export default FuelTrackingSection;
